import React, { useEffect, useMemo, memo, useCallback, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  getDataLoading,
  getCardError,
  getVisibleFormData,
  getCardStages,
  getParentId,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { HStack } from '../../../ui/Stack'
import CardTabs from './ui/tabs/tabs'
import { getClaimWorkData } from '../../../store/claim_work/action-creators'
import { clearClaimWorkData } from '../../../store/claim_work/actions'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { FormsHeader } from './ui/forms/form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { getStagePlanData } from '../../../store/stagePlan/action-creators'

function ClaimWorkCard() {
  const { id } = useParams<{ id: string }>()
  const parentId = useSelector(getParentId)
  const pageTitle = useMemo(
    () => (parentId === null ? `Работа №${id}` : `Работа по рекламации №${id}`),
    [id, parentId],
  )
  const dispatch = useAppDispatch()
  const entityType = 'claim_work'
  const dataIsLoading = useSelector(getDataLoading)
  const cardError = useSelector(getCardError)
  const visibleFormData = useSelector(getVisibleFormData)
  const stages = useSelector(getCardStages)
  const { saveResult } = useSaveResult()
  const [isStageTransitioning, setIsStageTransitioning] = useState(false)
  const stageTransitionLockRef = useRef(false)

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/claims-works',
        title: 'Работы по рекламациям',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted && id) {
      const getInitPage = async (id: string) => {
        dispatch(getClaimWorkData(id))
      }
      getInitPage(id).catch(console.error)
    }
    return () => {
      dispatch(clearClaimWorkData())
      mounted = false
    }
  }, [id, dispatch])

  const handleNextStageOnClick = useCallback(
    async (stage: any) => {
      if (stageTransitionLockRef.current) return
      stageTransitionLockRef.current = true
      setIsStageTransitioning(true)
      try {
        await saveResult(id, visibleFormData, stage, true, stages)
      } finally {
        if (id) {
          dispatch(getStagePlanData('claim_work', id))
        }
        stageTransitionLockRef.current = false
        setIsStageTransitioning(false)
      }
    },
    [id, visibleFormData, saveResult, dispatch, stages],
  )

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {cardError && !dataIsLoading ? (
        <HStack gap={'8'} className={'mapWrapper'}>
          При выполнении запроса возникла ошибка.
          <br />
          {cardError}
          <br />
          Попробуйте перезагрузить страницу или открыть обращение повторно.
        </HStack>
      ) : (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <FormsHeader
            id={id}
            nextStageOnClick={handleNextStageOnClick}
            isStageTransitioning={isStageTransitioning}
          />
          <CardTabs id={id} />
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
}

ClaimWorkCard.displayName = 'ClaimWorkCard'
export default memo(ClaimWorkCard)
